import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, Search, Eye, Pencil, Trash2, X, ChevronLeft, ChevronRight, UserCircle2, CreditCard, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { printCitizenCard } from '@/utils/pdfExport';

const STATUS_BADGE = { active: 'badge-active', moved: 'badge-inactive', deceased: 'bg-red-100 text-red-700' };
const CITIZEN_TYPE_BADGE = {
    warga: 'bg-blue-100 text-blue-700',
    pendatang: 'bg-orange-100 text-orange-700',
    domisili: 'bg-purple-100 text-purple-700',
};
const CITIZEN_TYPE_LABEL = { warga: 'Warga', pendatang: 'Pendatang', domisili: 'Domisili' };
const RELATIONSHIPS = ['Kepala Keluarga','Istri/Suami','Anak','Orang Tua','Mertua','Saudara','Lainnya'];
const MONTHS = ['','Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

const emptyMember = { nik: '', name: '', gender: 'male', birth_date: '', relationship: 'Anak', occupation: '', education: '', phone: '' };
const emptyCitizen = { kk_number: '', head_of_family: '', address: '', phone: '', email: '', rt: '001', rw: '001', status: 'active', members: [], citizen_type: 'warga', has_business: false, is_underprivileged: false, business_addition: 0, underprivileged_reduction: 0 };

export default function CitizensPage() {
    const { hasRole } = useAuth();
    const { t } = useLang();
    const [citizens, setCitizens] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showCard, setShowCard] = useState(null);
    const [editData, setEditData] = useState(null);
    const [form, setForm] = useState(emptyCitizen);
    const [saving, setSaving] = useState(false);
    const [regional, setRegional] = useState(null);
    const [iuranData, setIuranData] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const LIMIT = 20;
    const canEdit = hasRole('sekretaris', 'ketua_rt', 'wakil');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/citizens`, { params: { q, page, limit: LIMIT }, withCredentials: true });
            setCitizens(res.data.data);
            setTotal(res.data.total);
        } catch { toast.error('Gagal memuat data warga'); }
        setLoading(false);
    }, [q, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        axios.get(`${API}/settings/regional`, { withCredentials: true }).then(r => setRegional(r.data)).catch(() => {});
        axios.get(`${API}/citizens/addresses`, { withCredentials: true }).then(r => setAddresses(r.data || [])).catch(() => {});
    }, []);

    const openAdd = () => { setEditData(null); setForm(emptyCitizen); setShowForm(true); };
    const openEdit = (c) => {
        setEditData(c);
        setForm({ kk_number: c.kk_number, head_of_family: c.head_of_family, address: c.address,
                  phone: c.phone || '', email: c.email || '', rt: c.rt || '001', rw: c.rw || '001',
                  status: c.status || 'active', members: c.members || [],
                  citizen_type: c.citizen_type || 'warga',
                  has_business: c.has_business || false,
                  is_underprivileged: c.is_underprivileged || false,
                  business_addition: c.business_addition || 0,
                  underprivileged_reduction: c.underprivileged_reduction || 0 });
        setShowForm(true);
    };

    const openCard = async (c) => {
        setShowCard(c);
        try {
            const year = new Date().getFullYear();
            const res = await axios.get(`${API}/finance/iuran`, { params: { year, limit: 100 }, withCredentials: true });
            setIuranData(res.data.data.filter(i => i.citizen_id === c.id || i.kk_number === c.kk_number));
        } catch {}
    };

    const handleSave = async () => {
        if (!form.kk_number || !form.head_of_family || !form.address) {
            toast.error('Isi data wajib: No. KK, Kepala Keluarga, Alamat');
            return;
        }
        setSaving(true);
        try {
            if (editData) {
                await axios.put(`${API}/citizens/${editData.id}`, form, { withCredentials: true });
                toast.success('Data warga diperbarui');
            } else {
                await axios.post(`${API}/citizens`, form, { withCredentials: true });
                toast.success('Warga baru ditambahkan');
            }
            setShowForm(false); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan data'); }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Hapus data warga ini?')) return;
        try {
            await axios.delete(`${API}/citizens/${id}`, { withCredentials: true });
            toast.success('Data warga dihapus'); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menghapus'); }
    };

    const addMember = () => setForm(f => ({ ...f, members: [...f.members, { ...emptyMember }] }));
    const updateMember = (idx, field, val) => setForm(f => {
        const m = [...f.members]; m[idx] = { ...m[idx], [field]: val }; return { ...f, members: m };
    });
    const removeMember = (idx) => setForm(f => ({ ...f, members: f.members.filter((_, i) => i !== idx) }));

    const totalPages = Math.ceil(total / LIMIT);
    const year = new Date().getFullYear();

    return (
        <div className="space-y-5" data-testid="citizens-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl">{t.citizenData}</h1>
                    <p className="text-sm text-slate-500 mt-1">{total} {t.totalData}</p>
                </div>
                {canEdit && (
                    <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-citizen-btn">
                        <Plus className="w-4 h-4" /> {t.addNew}
                    </Button>
                )}
            </div>

            {/* Search */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder={t.searchPlaceholder} value={q}
                        onChange={e => { setQ(e.target.value); setPage(1); }}
                        className="pl-9 h-9 border-slate-200" data-testid="citizen-search" />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50">
                                <TableHead className="font-semibold text-slate-700 w-8">#</TableHead>
                                <TableHead className="font-semibold text-slate-700">{t.headOfFamily}</TableHead>
                                <TableHead className="font-semibold text-slate-700 hidden md:table-cell">{t.kkNumber}</TableHead>
                                <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">{t.address}</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-center">{t.membersCount}</TableHead>
                                <TableHead className="font-semibold text-slate-700">{t.status}</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-right">{t.actions}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        {[...Array(7)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                                    </TableRow>
                                ))
                            ) : citizens.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-12 text-slate-400">
                                        <UserCircle2 className="w-10 h-10 mx-auto mb-2 text-slate-200" />
                                        {t.noData}
                                    </TableCell>
                                </TableRow>
                            ) : citizens.map((c, i) => (
                                <TableRow key={c.id} className="hover:bg-slate-50 transition-colors">
                                    <TableCell className="text-slate-400 text-sm">{(page - 1) * LIMIT + i + 1}</TableCell>
                                    <TableCell>
                                        <div>
                                            <p className="font-semibold text-slate-800">{c.head_of_family}</p>
                                            <p className="text-xs text-slate-400 md:hidden">{c.kk_number}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell font-mono text-sm text-slate-600">{c.kk_number}</TableCell>
                                    <TableCell className="hidden lg:table-cell text-sm text-slate-600 max-w-xs truncate">{c.address}</TableCell>
                                    <TableCell className="text-center">
                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-700 text-sm font-bold">
                                            {c.members?.length || 0}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGE[c.status] || 'badge-inactive'}`}>
                                                {c.status === 'active' ? t.active : c.status === 'moved' ? t.moved : t.deceased}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${CITIZEN_TYPE_BADGE[c.citizen_type] || CITIZEN_TYPE_BADGE.warga}`}>
                                                {CITIZEN_TYPE_LABEL[c.citizen_type] || 'Warga'}
                                            </span>
                                            {c.has_business && <span className="text-xs px-2 py-1 rounded-full font-medium bg-teal-100 text-teal-700">Usaha</span>}
                                            {c.is_underprivileged && <span className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-600">KM</span>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => openCard(c)} title="Lihat Kartu"
                                                className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors" data-testid={`view-card-${c.id}`}>
                                                <CreditCard className="w-4 h-4" />
                                            </button>
                                            {canEdit && <>
                                                <button onClick={() => openEdit(c)} title="Edit"
                                                    className="p-1.5 rounded hover:bg-amber-50 text-amber-600 transition-colors" data-testid={`edit-citizen-${c.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleDelete(c.id)} title="Hapus"
                                                    className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors" data-testid={`delete-citizen-${c.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-between">
                        <p className="text-sm text-slate-500">{t.page} {page} {t.of} {totalPages}</p>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editData ? t.editData : t.addNew} {t.citizenData}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>{t.familyCard} *</Label>
                                <Input value={form.kk_number} onChange={e => setForm(f => ({ ...f, kk_number: e.target.value }))}
                                    placeholder="1234567890123456" data-testid="form-kk-number" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.headOfFamily} *</Label>
                                <Input value={form.head_of_family} onChange={e => setForm(f => ({ ...f, head_of_family: e.target.value }))}
                                    data-testid="form-head-of-family" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.fullAddress} *</Label>
                            {addresses.length > 0 ? (
                                <div className="space-y-1">
                                    <Select value={addresses.includes(form.address) ? form.address : '__new__'}
                                        onValueChange={v => v !== '__new__' && setForm(f => ({ ...f, address: v }))}>
                                        <SelectTrigger data-testid="form-address-select"><SelectValue placeholder="Pilih alamat..." /></SelectTrigger>
                                        <SelectContent>
                                            {addresses.map(addr => <SelectItem key={addr} value={addr}>{addr}</SelectItem>)}
                                            <SelectItem value="__new__">+ Alamat baru...</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                                        placeholder="Atau ketik alamat baru" data-testid="form-address" />
                                </div>
                            ) : (
                                <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                                    data-testid="form-address" />
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label>{t.phone}</Label>
                                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                    placeholder="08xx-xxxx-xxxx" data-testid="form-phone" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.email}</Label>
                                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                    data-testid="form-email" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-1.5">
                                <Label>{t.rt}</Label>
                                <Input value={form.rt} onChange={e => setForm(f => ({ ...f, rt: e.target.value }))} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.rw}</Label>
                                <Input value={form.rw} onChange={e => setForm(f => ({ ...f, rw: e.target.value }))} />
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.status}</Label>
                                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                                    <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">{t.active}</SelectItem>
                                        <SelectItem value="moved">{t.moved}</SelectItem>
                                        <SelectItem value="deceased">{t.deceased}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {/* Citizen Type & Special Flags */}
                        <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Status Kependudukan</p>
                            <div className="space-y-1.5">
                                <Label>Jenis Warga</Label>
                                <Select value={form.citizen_type || 'warga'} onValueChange={v => setForm(f => ({ ...f, citizen_type: v }))}>
                                    <SelectTrigger data-testid="form-citizen-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="warga">Warga — Penduduk tetap</SelectItem>
                                        <SelectItem value="pendatang">Pendatang — Tinggal sementara/kontrak</SelectItem>
                                        <SelectItem value="domisili">Domisili — Hanya untuk administrasi</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={form.has_business}
                                        onChange={e => setForm(f => ({ ...f, has_business: e.target.checked, business_addition: e.target.checked ? f.business_addition : 0 }))}
                                        className="w-4 h-4 rounded mt-0.5" data-testid="form-has-business" />
                                    <span className="text-sm font-medium text-slate-700">Warga Berdagang / Usaha</span>
                                </label>
                                {form.has_business && (
                                    <div className="ml-6 flex items-center gap-2">
                                        <span className="text-xs text-slate-500">Tambahan iuran:</span>
                                        <span className="text-xs text-slate-400">Rp</span>
                                        <Input type="number" min="0" value={form.business_addition || 0}
                                            onChange={e => setForm(f => ({ ...f, business_addition: Number(e.target.value) }))}
                                            className="h-8 text-sm max-w-32" placeholder="0" data-testid="form-business-addition" />
                                        <span className="text-xs text-slate-400">/bulan</span>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input type="checkbox" checked={form.is_underprivileged}
                                        onChange={e => setForm(f => ({ ...f, is_underprivileged: e.target.checked, underprivileged_reduction: e.target.checked ? f.underprivileged_reduction : 0 }))}
                                        className="w-4 h-4 rounded mt-0.5" data-testid="form-is-underprivileged" />
                                    <span className="text-sm font-medium text-slate-700">Warga Kurang Mampu</span>
                                </label>
                                {form.is_underprivileged && (
                                    <div className="ml-6 flex items-center gap-2">
                                        <span className="text-xs text-slate-500">Pengurangan iuran:</span>
                                        <span className="text-xs text-slate-400">Rp</span>
                                        <Input type="number" min="0" value={form.underprivileged_reduction || 0}
                                            onChange={e => setForm(f => ({ ...f, underprivileged_reduction: Number(e.target.value) }))}
                                            className="h-8 text-sm max-w-32" placeholder="0" data-testid="form-underprivileged-reduction" />
                                        <span className="text-xs text-slate-400">/bulan</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Family Members */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-semibold">{t.familyMembers} ({form.members.length})</Label>
                                <Button variant="outline" size="sm" onClick={addMember} className="gap-1 text-xs h-7">
                                    <Plus className="w-3.5 h-3.5" /> {t.addMember}
                                </Button>
                            </div>
                            {form.members.map((m, idx) => (
                                <div key={idx} className="border border-slate-200 rounded-lg p-3 mb-2 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-slate-600">Anggota {idx + 1}</p>
                                        <button onClick={() => removeMember(idx)} className="text-red-400 hover:text-red-600 transition-colors">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs">{t.memberName}</Label>
                                            <Input size="sm" className="h-8 text-sm" value={m.name}
                                                onChange={e => updateMember(idx, 'name', e.target.value)} />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">{t.memberNik}</Label>
                                            <Input size="sm" className="h-8 text-sm" value={m.nik}
                                                onChange={e => updateMember(idx, 'nik', e.target.value)} />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">{t.relationship}</Label>
                                            <Select value={m.relationship} onValueChange={v => updateMember(idx, 'relationship', v)}>
                                                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">{t.gender}</Label>
                                            <Select value={m.gender} onValueChange={v => updateMember(idx, 'gender', v)}>
                                                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="male">{t.male}</SelectItem>
                                                    <SelectItem value="female">{t.female}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1 col-span-2">
                                            <Label className="text-xs">No. Telepon (opsional)</Label>
                                            <Input size="sm" className="h-8 text-sm" value={m.phone || ''}
                                                onChange={e => updateMember(idx, 'phone', e.target.value)}
                                                placeholder="08xx-xxxx-xxxx (opsional)" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>{t.cancel}</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-citizen-btn">
                            {saving ? 'Menyimpan...' : t.save}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Digital ID Card Dialog */}
            <Dialog open={!!showCard} onOpenChange={() => setShowCard(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-primary" /> Kartu Iuran Digital
                        </DialogTitle>
                    </DialogHeader>
                    {showCard && (
                        <div className="space-y-4">
                            {/* Card */}
                            <div className="id-card p-5 text-white">
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <p className="text-xs opacity-70 tracking-widest uppercase">Kartu Iuran Warga</p>
                                            {regional?.rt_number && (
                                                <p className="text-sm font-bold">RT {regional.rt_number} / RW {regional.rw_number}</p>
                                            )}
                                            {regional?.kelurahan && <p className="text-xs opacity-70">{regional.kelurahan}, {regional.kecamatan}</p>}
                                        </div>
                                        {regional?.logo_url ? (
                                            <img src={regional.logo_url} alt="Logo" className="w-12 h-12 object-contain bg-white/20 rounded-lg p-1" />
                                        ) : (
                                            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                                                <UserCircle2 className="w-7 h-7 opacity-80" />
                                            </div>
                                        )}
                                    </div>
                                    <h3 className="text-lg font-extrabold">{showCard.head_of_family}</h3>
                                    <p className="text-xs opacity-70 mt-0.5 font-mono">KK: {showCard.kk_number}</p>
                                    <p className="text-xs opacity-70 mt-1">{showCard.address}</p>
                                </div>
                            </div>
                            {/* Payment status */}
                            <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Status Iuran {year}</p>
                                <div className="grid grid-cols-6 gap-1">
                                    {MONTHS.slice(1).map((mn, i) => {
                                        const paid = iuranData.some(d => d.month === i + 1 && d.year === year && d.status === 'paid');
                                        return (
                                            <div key={i} className={`rounded p-1.5 text-center ${paid ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
                                                <p className="text-xs font-bold">{mn.substring(0, 3)}</p>
                                                <p className="text-xs">{paid ? '✓' : '–'}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* Members */}
                            {showCard.members?.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                        Anggota Keluarga ({showCard.members.length})
                                    </p>
                                    <ul className="space-y-1">
                                        {showCard.members.map((m, i) => (
                                            <li key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2">
                                                <span className="font-medium text-slate-700">{m.name}</span>
                                                <span className="text-xs text-slate-400">{m.relationship}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {/* Print button */}
                            <Button
                                onClick={() => printCitizenCard(showCard, regional, iuranData, year)}
                                className="w-full gap-2 bg-primary hover:bg-primary/90"
                                data-testid="print-citizen-card-btn">
                                <Printer className="w-4 h-4" /> Cetak Kartu Iuran (PDF)
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
